import { uploadAPI } from './api';

export interface UploadResult {
  url: string;
  key: string;
  bucket: string;
  format: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

class UploadService {
  /**
   * Upload a single image file
   */
  async uploadImage(
    file: File, 
    _onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await uploadAPI.uploadImage(formData);
      return (response.data as any).data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload image');
    }
  }

  /**
   * Upload multiple images
   */
  async uploadMultipleImages(
    files: File[], 
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult[]> {
    const uploadPromises = files.map(file => this.uploadImage(file, onProgress));
    return Promise.all(uploadPromises);
  }

  /**
   * Upload a document file
   */
  async uploadDocument(
    file: File, 
    _onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult> {
    const formData = new FormData();
    formData.append('document', file);

    try {
      const response = await uploadAPI.uploadDocument(formData);
      return (response.data as any).data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload document');
    }
  }

  /**
   * Upload store images (logo, banner, gallery)
   */
  async uploadStoreImages(
    storeId: string,
    files: { logo?: File; banner?: File; images?: File[] },
    _onProgress?: (progress: UploadProgress) => void
  ): Promise<any> {
    const formData = new FormData();
    
    if (files.logo) {
      formData.append('logo', files.logo);
    }
    
    if (files.banner) {
      formData.append('banner', files.banner);
    }
    
    if (files.images && files.images.length > 0) {
      files.images.forEach((image) => {
        formData.append('images', image);
      });
    }

    try {
      const response = await uploadAPI.uploadStoreImages(storeId, formData);
      return (response.data as any).data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload store images');
    }
  }

  /**
   * Upload listing images
   */
  async uploadListingImages(
    listingId: string,
    images: File[],
    _onProgress?: (progress: UploadProgress) => void
  ): Promise<any> {
    const formData = new FormData();
    
    images.forEach((image) => {
      formData.append('images', image);
    });

    try {
      const response = await uploadAPI.uploadListingImages(listingId, formData);
      return (response.data as any).data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload listing images');
    }
  }

  /**
   * Validate file type
   */
  validateImageFile(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    return allowedTypes.includes(file.type);
  }

  /**
   * Validate file size (max 10MB)
   */
  validateFileSize(file: File, maxSizeMB: number = 10): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Get file preview URL
   */
  getFilePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke file preview URL
   */
  revokeFilePreview(url: string): void {
    URL.revokeObjectURL(url);
  }
}

export const uploadService = new UploadService();
export default uploadService;
